
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";

import { useProjects } from "@/hooks/useProjects";
import { useToast } from "@/hooks/use-toast";
import { DollarSign, Clock, FileText, Plus, Trash } from "lucide-react";
import { PricingComponent, InvoiceSettings, Project } from "@/types/Project";
import { supabase } from "@/integrations/supabase/client";

interface ProjectFormData {
  name: string;
  description?: string;
  hourlyRate: number;
  isArchived: boolean;
  is_team_project: boolean;
  user_id: string;
  owner_id: string;
  pricingComponents?: PricingComponent[];
  invoiceSettings?: InvoiceSettings;
}

interface ProjectFormProps {
  onSuccess?: (project: any) => void;
  initialData?: any;
  projectId?: string;
}

export function ProjectForm({ onSuccess, initialData, projectId }: ProjectFormProps) {
  const { createProjectAsync } = useProjects();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [hourlyRate, setHourlyRate] = useState("300");
  
  const [pricingComponents, setPricingComponents] = useState<PricingComponent[]>([]);
  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettings>({
    clientName: "",
    clientEmail: "",
    clientAddress: "",
    paymentTerms: "Net 30",
    paymentMethods: "",
    bankDetails: "",
    notes: ""
  });

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description || "");
      setHourlyRate(initialData.hourlyRate.toString());
      
      
      if (initialData.pricingComponents && initialData.pricingComponents.length > 0) {
        setPricingComponents(initialData.pricingComponents);
      }
      
      if (initialData.invoiceSettings) {
        setInvoiceSettings(initialData.invoiceSettings);
      }
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate authentication before proceeding
    const { data: authUser, error: authError } = await supabase.auth.getUser();
    
    if (authError) {
      console.error('Authentication error:', authError);
      toast({
        title: "Authentication Error",
        description: "There was an issue with your authentication. Please try signing in again.",
        variant: "destructive",
      });
      return;
    }

    if (!authUser.user) {
      console.error('No authenticated user found');
      toast({
        title: "Authentication required",
        description: "Please sign in to create a project.",
        variant: "destructive",
      });
      // Optional: navigate to auth page
      // window.location.href = '/auth';
      return;
    }

    // Validate required fields
    if (!name.trim()) {
      toast({
        title: "Validation Error",
        description: "Project name is required.",
        variant: "destructive",
      });
      return;
    }

    if (isNaN(parseFloat(hourlyRate)) || parseFloat(hourlyRate) < 0) {
      toast({
        title: "Validation Error", 
        description: "Please enter a valid hourly rate.",
        variant: "destructive",
      });
      return;
    }

    const data: ProjectFormData = {
      name: name.trim(),
      description: description.trim() || undefined,
      hourlyRate: parseFloat(hourlyRate),
      isArchived: false,
      is_team_project: false,
      user_id: authUser.user.id,
      owner_id: authUser.user.id,
      pricingComponents,
      invoiceSettings
    };

    console.log('=== PROJECT CREATION DEBUG ===');
    console.log('Authenticated user ID:', authUser.user.id);
    console.log('Session info:', { 
      hasSession: !!authUser.user, 
      userId: authUser.user.id,
      email: authUser.user.email 
    });
    console.log('Project data:', {
      name: data.name,
      user_id: data.user_id,
      owner_id: data.owner_id,
      hourlyRate: data.hourlyRate
    });
    console.log('==============================');

    setIsSubmitting(true);
    
    try {
      await createProjectAsync(data);
      
      // Reset form on success
      setName("");
      setDescription("");
      setHourlyRate("300");
      setPricingComponents([]);
      setInvoiceSettings({
        clientName: "",
        clientEmail: "",
        clientAddress: "",
        paymentTerms: "Net 30",
        paymentMethods: "",
        bankDetails: "",
        notes: ""
      });

      // Call success callback
      if (onSuccess) {
        onSuccess(data);
      }
      
    } catch (error: any) {
      console.error('Project creation error:', error);
      
      if (error.message.includes('auth') || error.message.includes('authentication')) {
        toast({
          title: "Authentication Error",
          description: "Please sign out and sign back in, then try again.",
          variant: "destructive",
        });
      } else if (error.message.includes('policy')) {
        toast({
          title: "Permission Error", 
          description: "You don't have permission to create this project. Please try signing in again.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error Creating Project",
          description: error.message || "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const addPricingComponent = () => {
    const newComponent: PricingComponent = {
      id: crypto.randomUUID(),
      name: "",
      pricingType: "hourly",
      amount: 300
    };
    setPricingComponents([...pricingComponents, newComponent]);
  };

  const updatePricingComponent = (id: string, field: keyof PricingComponent, value: any) => {
    setPricingComponents(pricingComponents.map(component => 
      component.id === id ? { ...component, [field]: value } : component
    ));
  };

  const removePricingComponent = (id: string) => {
    setPricingComponents(pricingComponents.filter(component => component.id !== id));
  };
  
  const updateInvoiceSettings = (field: keyof InvoiceSettings, value: string) => {
    setInvoiceSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Project Name</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter project name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Description (Optional)</label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter project description"
            className="min-h-[100px]"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Default Hourly Rate ($)</label>
          <Input
            type="number"
            min="0"
            step="0.01"
            value={hourlyRate}
            onChange={(e) => setHourlyRate(e.target.value)}
            placeholder="Enter hourly rate"
            required
          />
        </div>


        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="pricing">
            <AccordionTrigger className="text-sm font-medium">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                <span>Additional Pricing Components</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={addPricingComponent}
                    className="flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Component
                  </Button>
                </div>
                
                {pricingComponents.length > 0 && (
                  <div className="space-y-3 mt-2">
                    {pricingComponents.map((component) => (
                      <Card key={component.id} className="relative">
                        <CardContent className="pt-4 pb-2">
                          <div className="flex items-start gap-2 mb-2">
                            <div className="flex-1">
                              <label className="text-xs font-medium mb-1 block">Component Name</label>
                              <Input
                                value={component.name}
                                onChange={(e) => updatePricingComponent(component.id, 'name', e.target.value)}
                                placeholder="e.g., Design, Development, etc."
                                required
                              />
                            </div>
                            <Button 
                              type="button" 
                              variant="ghost" 
                              size="icon"
                              className="h-9 w-9 text-destructive hover:text-destructive hover:bg-destructive/10 -mt-1"
                              onClick={() => removePricingComponent(component.id)}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs font-medium mb-1 block">Pricing Type</label>
                              <Select
                                value={component.pricingType}
                                onValueChange={(value) => updatePricingComponent(component.id, 'pricingType', value)}
                              >
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select pricing type" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="hourly">
                                    <div className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      <span>Hourly Rate</span>
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="fixed">
                                    <div className="flex items-center gap-1">
                                      <DollarSign className="h-3 w-3" />
                                      <span>Fixed Price</span>
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-xs font-medium mb-1 block">
                                {component.pricingType === 'hourly' ? 'Hourly Rate ($)' : 'Fixed Price ($)'}
                              </label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={component.amount.toString()}
                                onChange={(e) => updatePricingComponent(component.id, 'amount', parseFloat(e.target.value))}
                                required
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="invoice">
            <AccordionTrigger className="text-sm font-medium">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <span>Invoice Settings</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 mt-2">
                <div>
                  <label className="text-xs font-medium mb-1 block">Client Name</label>
                  <Input
                    value={invoiceSettings.clientName || ""}
                    onChange={(e) => updateInvoiceSettings('clientName', e.target.value)}
                    placeholder="Enter client name"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block">Client Email</label>
                  <Input
                    type="email"
                    value={invoiceSettings.clientEmail || ""}
                    onChange={(e) => updateInvoiceSettings('clientEmail', e.target.value)}
                    placeholder="Enter client email"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block">Client Address</label>
                  <Textarea
                    value={invoiceSettings.clientAddress || ""}
                    onChange={(e) => updateInvoiceSettings('clientAddress', e.target.value)}
                    placeholder="Enter client address"
                    className="min-h-[80px]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block">Payment Terms</label>
                  <Input
                    value={invoiceSettings.paymentTerms || ""}
                    onChange={(e) => updateInvoiceSettings('paymentTerms', e.target.value)}
                    placeholder="e.g., Net 30"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block">Payment Methods</label>
                  <Textarea
                    value={invoiceSettings.paymentMethods || ""}
                    onChange={(e) => updateInvoiceSettings('paymentMethods', e.target.value)}
                    placeholder="e.g., Bank Transfer, PayPal"
                    className="min-h-[80px]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block">Bank Details</label>
                  <Textarea
                    value={invoiceSettings.bankDetails || ""}
                    onChange={(e) => updateInvoiceSettings('bankDetails', e.target.value)}
                    placeholder="Enter bank account details"
                    className="min-h-[80px]"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block">Additional Notes for Invoice</label>
                  <Textarea
                    value={invoiceSettings.notes || ""}
                    onChange={(e) => updateInvoiceSettings('notes', e.target.value)}
                    placeholder="Enter any additional notes for invoices"
                    className="min-h-[80px]"
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Creating..." : (projectId ? "Update Project" : "Create Project")}
        </Button>
      </form>
    </>
  );
}
